import {
  StoriesApi,
  StoryResponse,
  StoryListResponse,
  StoryAuthorsResponse,
  CreateStoryRequest,
  UpdateStoryRequest,
  LinkStoryMediaRequest,
  StoryMediaAttachmentResponse,
  StorySortBy,
  SearchStoriesSortOrderEnum,
  ReviewStoryRequest
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

export interface SearchStoriesOptions {
  query?: string;
  familyMemberId?: string;
  authorUserId?: string;
  excludeId?: string;
  status?: string;
  hasAttachments?: boolean;
  sortBy?: StorySortBy;
  sortOrder?: SearchStoriesSortOrderEnum;
  page?: number;
  pageSize?: number;
}

export type { StoryAuthorsResponse };

class WrappedStoriesApi {
  async searchStories(familyId: string, options: SearchStoriesOptions = {}): Promise<ApiResult<StoryListResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) =>
        api.searchStories(
          familyId,
          options.query,
          options.familyMemberId,
          options.authorUserId,
          options.excludeId,
          options.status,
          options.hasAttachments,
          options.sortBy,
          options.sortOrder,
          options.page,
          options.pageSize
        )
    );
  }

  async listStoryAuthors(familyId: string): Promise<ApiResult<StoryAuthorsResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.listStoryAuthors(familyId)
    );
  }

  async getStory(familyId: string, storyId: string): Promise<ApiResult<StoryResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.getStory(familyId, storyId)
    );
  }

  async createStory(familyId: string, request: CreateStoryRequest): Promise<ApiResult<StoryResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.createStory(familyId, request)
    );
  }

  async updateStory(familyId: string, storyId: string, request: UpdateStoryRequest): Promise<ApiResult<StoryResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.updateStory(familyId, storyId, request)
    );
  }

  async deleteStory(familyId: string, storyId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.deleteStory(familyId, storyId)
    );
  }

  async linkStoryMedia(
    familyId: string,
    storyId: string,
    request: LinkStoryMediaRequest
  ): Promise<ApiResult<StoryMediaAttachmentResponse[]>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.linkStoryMedia(familyId, storyId, request)
    );
  }

  async unlinkStoryMedia(familyId: string, storyId: string, mediaRefId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.unlinkStoryMedia(familyId, storyId, mediaRefId)
    );
  }

  async approveStory(
    familyId: string,
    storyId: string,
    request?: ReviewStoryRequest
  ): Promise<ApiResult<StoryResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.approveStory(familyId, storyId, request)
    );
  }

  async rejectStory(
    familyId: string,
    storyId: string,
    request?: ReviewStoryRequest
  ): Promise<ApiResult<StoryResponse>> {
    return withApiSetup(
      (config) => new StoriesApi(config),
      getApiConfiguration,
      (api) => api.rejectStory(familyId, storyId, request)
    );
  }
}

export const storiesApi = new WrappedStoriesApi();

export default storiesApi;
